import { rest } from '@0xtorch/core'
import { z } from 'zod'
import { fetchDataWithAutoRetry } from '../../utils/fetchDataWithAutoRetry'
import { bybitApiEndpoint } from '../constants'
import { commonResponseSchema, successReponseSchema } from '../schemas'
import { createPrivateGetHeaders } from '../utils'
import { type Trade, resultSchema } from './schema'

const responseSchema = successReponseSchema.extend({
  result: resultSchema,
})

const urlPath = '/v5/execution/list'

type FetchTradeHistoriesParameters = {
  apiKey: string
  apiSecret: string
  category: 'linear' | 'inverse' | 'spot' | 'option'
  from: number
  to: number
  limit: number
  proxy?: string
  headers?: Record<string, string>
}

/**
 * - Unified account supports getting the past 730 days historical trades data
 * - the rule is endTime - startTime <= 7 days
 */
export const fetchTradeHistories = ({
  apiKey,
  apiSecret,
  category,
  from,
  to,
  limit,
  proxy,
  headers,
}: FetchTradeHistoriesParameters): Promise<readonly Trade[]> =>
  fetchDataWithAutoRetry({
    from,
    to,
    limit,
    desc: true,
    fetchData: async ({ from, to, limit, desc }) => {
      if (!desc) {
        throw new Error('desc must be true')
      }
      if (limit > 100) {
        throw new Error('limit must be less than or equal to 100')
      }

      const url = new URL(urlPath, bybitApiEndpoint)
      url.searchParams.append('category', category)
      url.searchParams.append('limit', limit.toString())
      url.searchParams.append('startTime', from.toString())
      url.searchParams.append('endTime', to.toString())
      const query = url.searchParams.toString()

      const response = await rest(
        proxy === undefined
          ? url.toString()
          : `${proxy}${encodeURIComponent(url.toString())}`,
        {
          fetchOptions: {
            method: 'GET',
            headers: {
              ...headers,
              ...(await createPrivateGetHeaders({
                apiKey,
                apiSecret,
                query: query.length > 0 ? query : '',
              })),
            },
          },
          schema: z.union([responseSchema, commonResponseSchema]),
        },
      )

      if (!('result' in response)) {
        throw new Error(response.retMsg)
      }
      return response.result.list
    },
  })
